import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';

import PATHS from 'utils/paths';

import {
	faChartBar,
	faChartColumn,
	faChartLine,
	faChartPie,
	faCheck,
	faClone,
	faEdit,
	faLayerGroup,
	faPlusCircle,
	faQuestion,
	faTable,
	faTrashCan
} from '@fortawesome/pro-regular-svg-icons';

import Tooltip from '../Tooltip';
import Typography from '../Typography';
import * as S from '../styled/ManageWidgetsModal.styled';
import { ANALYSIS_PAGES, WIDGET_TYPES } from './widgetsUtils';

const WidgetContent = ({ widget, onCheck, checked, onPagesChange, onWidgetsOrderChange, pages, setLocalPages }) => {
	const [selectedPagesPerWidget, setSelectedPagesPerWidget] = useState(widget.pages || []);

	const { pathname } = useLocation();
	const isMonitorView =
		pathname.startsWith(PATHS.DASHBOARD_MONITOR_VIEW) || pathname.startsWith(PATHS.ANALYSIS_MONITOR_VIEW);

	const pageTagConfig = {
		[ANALYSIS_PAGES.dashboard]: { bg: 'blue' },
		[ANALYSIS_PAGES.overview]: { bg: 'green' },
		[ANALYSIS_PAGES.SLAPerformance]: { bg: 'teel' },
		[ANALYSIS_PAGES.deliveryCompany]: { bg: 'red' },
		[ANALYSIS_PAGES.otoFlex]: { bg: 'forest' },
		[ANALYSIS_PAGES.shippingAccounting]: { bg: 'yellow' },
		[ANALYSIS_PAGES.sales]: { bg: 'pink' }
	};

	const renderPreviewImage = useMemo(() => {
		const previewConfig = {
			[WIDGET_TYPES.pie]: { bg: '#60A4EA', icon: faChartPie },
			[WIDGET_TYPES.bar]: { bg: '#88C58F', icon: faChartBar },
			[WIDGET_TYPES.line]: { bg: '#EA995F', icon: faChartLine },
			[WIDGET_TYPES.column]: { bg: '#F58FA9', icon: faChartColumn },
			[WIDGET_TYPES.table]: { bg: '#958EF4', icon: faTable },
			[WIDGET_TYPES.cards]: { bg: '#ECC37B', icon: faLayerGroup }
		};

		const config = previewConfig[widget.type];

		return (
			<S.WidgetPreview backgroundColor={config?.bg}>
				<S.ChartPreviewIcon icon={config?.icon ?? faQuestion} size="large" color="unset" />
			</S.WidgetPreview>
		);
	}, [widget.type]);

	const handleResize = (size) => {
		onWidgetsOrderChange((prevState) => {
			const tempState = structuredClone(prevState);
			const widgetIndex = tempState.findIndex((prevStateWidget) => prevStateWidget.id === widget.id);
			tempState[widgetIndex].size = size;
			return tempState;
		});
	};

	const handleCreateCopy = () => {
		const newWidget = {
			...widget,
			id: `${widget.id}-${dayjs().format('YYYYMMDDHHmmssSSS')}`,
			order: widget.order + 1,
			isDuplicate: true,
			originalId: widget.id
		};
		onWidgetsOrderChange((prevState) => {
			const tempState = structuredClone(prevState);
			const originalIndex = tempState.findIndex((w) => w.id === widget.id);
			tempState.splice(originalIndex + 1, 0, newWidget);
			return tempState.map((item, idx) => ({ ...item, order: idx }));
		});
	};

	const [isEditingName, setIsEditingName] = useState(false);
	const [newName, setNewName] = useState();
	const [nameError, setNameError] = useState('');

	const handleEditDuplicate = () => {
		if (isEditingName) {
			if (!newName?.trim()) {
				setNameError('widgetNameCannotBeEmpty');
				return;
			}
			setNameError('');
			onWidgetsOrderChange((prevState) => {
				const tempState = structuredClone(prevState);
				const originalIndex = tempState.findIndex((w) => w.id === widget.id);
				if (originalIndex !== -1) {
					tempState[originalIndex].name = newName;
				}
				return tempState.map((item, idx) => ({ ...item, order: idx }));
			});
		} else {
			setIsEditingName(true);
		}
	};

	const handleDeleteCopy = () => {
		onWidgetsOrderChange((prevState) => {
			const tempState = structuredClone(prevState);
			const originalIndex = tempState.findIndex((w) => w.id === widget.id);
			tempState.splice(originalIndex, 1);
			return tempState.map((item, idx) => ({ ...item, order: idx }));
		});
	};

	return (
		<S.WidgetContentWrapper>
			<S.WidgetHeader isEditingName>
				{renderPreviewImage}
				<S.TextContainer>
					{isEditingName ? (
						<div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }} className="mb-4">
							<div>
								<StringInput
									value={newName}
									onChange={(e) => {
										setNewName(e.target.value);
										if (nameError) setNameError('');
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleEditDuplicate();
											if (newName?.trim()) setIsEditingName(false);
										}
									}}
									error={!!nameError}
								/>
								{nameError && (
									<Typography variant="p3" color="var(--red)">
										{nameError}
									</Typography>
								)}
							</div>
							<Tooltip placement="top" title={'saveWidgetTitle'}>
								<Button
									iconButton
									icon={faCheck}
									variant="blueBorderless"
									onClick={() => {
										handleEditDuplicate();
										if (newName?.trim()) setIsEditingName(false);
									}}
								/>
							</Tooltip>
						</div>
					) : (
						<Typography variant="p1" gutterBottom noWrap>
							{widget.name}
						</Typography>
					)}
					{widget.sourcePage && (
						<div>
							<S.PageTag backgroundColor={pageTagConfig[widget.sourcePage]?.bg}>
								<Typography variant="p3" fontWeight="500" noWrap>
									{widget.sourcePage}
								</Typography>
							</S.PageTag>
						</div>
					)}
				</S.TextContainer>
				{!widget.isDuplicate && !isEditingName && (
					<Tooltip placement="top" title={'duplicateWidget'}>
						<S.EditorButton icon={faClone} onClick={handleCreateCopy} variant={'borderless'} />
					</Tooltip>
				)}
				{widget.isDuplicate && !isEditingName && (
					<Tooltip placement="top" title={'editWidgetTitle'}>
						<S.EditorButton icon={faEdit} onClick={handleEditDuplicate} variant={'blueBorderless'} />
					</Tooltip>
				)}
				{widget.isDuplicate && !isEditingName && (
					<Tooltip placement="top" title={'deleteDuplicateWigdet'}>
						<S.EditorButton
							icon={faTrashCan}
							onClick={handleDeleteCopy}
							variant={'borderless'}
							color="var(--red)"
							className="delete-copy-button"
						/>
					</Tooltip>
				)}
				{!isEditingName && (
					<Tooltip placement="top" title={'showWigdet'}>
						<S.EditorButton
							icon={checked ? faCheck : faPlusCircle}
							onClick={onCheck}
							variant={checked ? 'blue' : 'outline'}
						/>
					</Tooltip>
				)}
			</S.WidgetHeader>
			{isMonitorView && (
				<S.WidgetOptionsWrapper>
					<S.Option>
						<Typography variant="p3" gutterBottom fontWeight="500">
							showOnPages
						</Typography>
						<EnumSelect
							data={pages || []}
							setValue={(value) => {
								onPagesChange(widget.id, value);
								const flattenPages = (p) =>
									Array.isArray(p)
										? p.flat(Infinity).filter((pg) => typeof pg === 'string')
										: typeof p === 'string'
											? [p]
											: [];

								const flatValue = flattenPages(value);
								setSelectedPagesPerWidget(flatValue);
							}}
							value={selectedPagesPerWidget || []}
							firstItem={{
								action: () => {
									const lastPage =
										pages && pages.length > 0
											? Math.max(...pages.map((p) => Number(p)).filter((n) => !isNaN(n)))
											: 0;
									const newPage = String(lastPage + 1);
									setLocalPages((prev) => [...prev, newPage]);
								},
								label: <Typography color="var(--blue)">addNewPage</Typography>,
								shouldKeepOpen: true
							}}
							multipleSelection
							renderSelectionValue={(value) => (
								<Typography>
									<Trans
										i18nKey="pageNumber"
										values={{
											count: value
										}}
									/>
								</Typography>
							)}
							renderOption={(value) => (
								<Typography>
									<Trans
										i18nKey="pageNumber"
										values={{
											count: value
										}}
									/>
								</Typography>
							)}
						/>
					</S.Option>
					<S.Option>
						<Typography variant="p3" gutterBottom fontWeight="500">
							widgetSize
						</Typography>
						<EnumSelect
							data={['small', 'medium', 'large']}
							setValue={(value) => {
								handleResize(value);
							}}
							value={widget.size || 'medium'}
							searchable={false}
						/>
					</S.Option>
				</S.WidgetOptionsWrapper>
			)}
		</S.WidgetContentWrapper>
	);
};

export default WidgetContent;
